import { IControl } from "mapbox-gl";

export interface IconButtonControlOptions {
    name: string;
    buttonClassName: string;
}

export class IconButtonControl implements IControl {
    _element: HTMLElement;
    _button: HTMLButtonElement;
    _buttonClassName: string;

    get buttonClassName(): string {
        return this._buttonClassName;
    }

    set buttonClassName(value: string) {
        this._buttonClassName = value;
        this._button.className = value;
    }

    constructor(options: IconButtonControlOptions, onClick?: () => void) {
        this._element = document.createElement("div");
        this._element.className = "mapboxgl-ctrl mapboxgl-ctrl-group ";
        this._buttonClassName = options.buttonClassName;
        this._button = document.createElement("button");
        this._button.style.display = "flex";
        this._button.style.alignItems = "center";
        this._button.style.justifyContent = "center";
        this._button.className = options.buttonClassName;
        const iconSpan = document.createElement("span");
        iconSpan.className = "mapboxgl-ctrl-icon";
        this._button.appendChild(iconSpan);
        this._button.onclick = (e) => {
            e.stopPropagation();
            if (onClick) {
                onClick();
            }
        };

        this._element.appendChild(this._button);
    }

    onAdd(_: mapboxgl.Map): HTMLElement {
        return this._element;
    }

    onRemove(): void {
        this._element.parentNode?.removeChild(this._element);
    }
}

export interface ButtonOptions {
    textContent: string;
    disabled?: boolean;
    value?: string;
}

export interface ButtonGroupControlOptions {
    className?: string;
    selectedValue?: string;
    buttons: ButtonOptions[];
}

export class ButtonGroupControl implements IControl {
    _element: HTMLElement;

    get selectedValue(): string | undefined {
        const buttons = this._element.querySelectorAll("button");
        for (const btn of buttons) {
            if ((btn as HTMLElement).dataset.selected === "true") {
                return (btn as HTMLElement).dataset.value;
            }
        }
        return undefined;
    }

    set selectedValue(value: string | undefined) {
        const buttons = this._element.querySelectorAll("button");
        buttons.forEach((btn) => {
            if ((btn as HTMLElement).dataset.value === value) {
                (btn as HTMLElement).style.background = "rgba(0, 0, 0, 0.05)";
                (btn as HTMLElement).dataset.selected = "true";
            } else {
                (btn as HTMLElement).style.background = "inherit";
                (btn as HTMLElement).dataset.selected = "false";
            }
        });
    }

    set buttons(value: ButtonOptions[]) {
        const existingButtons = this._element.querySelectorAll("button");
        // Only update existing buttons. Value and ordering can’t change.
        for (const btn of existingButtons) {
            const buttonOpt = value.find(
                (b) => b.value === (btn as HTMLElement).dataset.value,
            );
            if (buttonOpt) {
                (btn as HTMLElement).textContent = buttonOpt.textContent;
                (btn as HTMLButtonElement).disabled =
                    buttonOpt.disabled || false;
            }
        }
    }

    constructor(
        options: ButtonGroupControlOptions,
        onClick?: (value: string) => void,
    ) {
        this._element = document.createElement("div");
        this._element.className =
            "mapboxgl-ctrl mapboxgl-ctrl-group " + (options.className || "");
        this._element.style.display = "flex";
        this._element.style.flexDirection = "row";
        var index = 0;
        for (const btnOption of options.buttons) {
            const button = document.createElement("button");
            button.style.width = "auto";
            button.style.padding = "0 8px";
            button.dataset.value = btnOption.value || "";
            button.dataset.selected = "false";
            if (index === 0) {
                button.style.borderRadius = "4px 0 0 4px";
            }
            if (index === options.buttons.length - 1) {
                button.style.borderRadius = "0 4px 4px 0";
            }
            if (
                options.selectedValue &&
                btnOption.value === options.selectedValue
            ) {
                button.style.background = "rgba(0, 0, 0, 0.05)";
                button.dataset.selected = "true";
            } else {
                button.style.background = "inherit";
                button.dataset.selected = "false";
            }
            button.textContent = btnOption.textContent;
            button.disabled = btnOption.disabled || false;
            button.onclick = (e) => {
                e.stopPropagation();
                if (onClick) {
                    onClick(btnOption.value || "");
                }
            };
            this._element.appendChild(button);
            index++;
        }
    }

    onAdd(_: mapboxgl.Map): HTMLElement {
        return this._element;
    }

    onRemove(): void {
        this._element.parentNode?.removeChild(this._element);
    }
}
